/*
 * @Description: 实时定位驾驶舱 — A2「成员列表」数据 hook（分页 + 上拉加载更多）
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { fetchRealtimeMembers } from '../../../actions/locationCockpit'
import { REALTIME_POLL_INTERVAL_MS, isCockpitMockEnabled } from '../constants'
import { MOCK_REALTIME_MEMBERS } from '../mock/members.mock'
import {
  CockpitEnvelope,
  RealtimeMemberItem,
  RealtimeMembersData
} from '../types/cockpit.types'

/** 默认每页条数（A2 分页 max 100） */
const DEFAULT_PAGE_SIZE = 20

export interface UseRealtimeMembersResult {
  members: RealtimeMemberItem[]
  /** 团队成员总数（含未绑定，标题展示用） */
  total: number
  /** 首页加载中 */
  loading: boolean
  /** 加载下一页中 */
  loadingMore: boolean
  /** 是否还有下一页 */
  hasMore: boolean
  /** 上拉加载下一页 */
  loadMore: () => void
  /** 手动刷新一次（重置到首页） */
  refresh: () => void
}

/**
 * 拉取实时定位驾驶舱 A2「成员列表」（右侧团队成员卡），分页累加。
 * 挂载 / 切日期即拉首页；上拉触底追加下一页；仅当天按 60s 轮询。
 * 轮询采用「就地合并首页」更新在线状态，不打断已加载的分页与滚动位置。
 * mock 模式一次性返回本地 fixtures，无分页。
 *
 * @param groupId - 当前团队长 id
 * @param date - 查询日期 YYYY-MM-DD（随顶部日期切换）
 * @param isToday - date 是否为当天（决定是否轮询；非当天暂停实时更新）
 * @param pageSize - 每页条数（默认 20；回放页只需下拉选项可传 100 一次拉全）
 */
export function useRealtimeMembers(
  groupId: string,
  date: string,
  isToday: boolean,
  pageSize: number = DEFAULT_PAGE_SIZE,
  pollIntervalMs: number = REALTIME_POLL_INTERVAL_MS
): UseRealtimeMembersResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [members, setMembers] = useState<RealtimeMemberItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const aliveRef = useRef(true)
  // 已加载到的最大页码
  const pageRef = useRef(1)
  // 防止滚动事件重复触发加载
  const loadingMoreRef = useRef(false)

  /** 拉取某页；mode：reset 重置 / append 追加 / merge 轮询就地更新已加载项 */
  const fetchPage = useCallback(
    (pageNo: number, mode: 'reset' | 'append' | 'merge') => {
      return (
        dispatch(fetchRealtimeMembers({ groupId, pageNo, pageSize, date }))
          .then((res: CockpitEnvelope<RealtimeMembersData>) => {
            if (!aliveRef.current || !res || res.code !== 200) return
            const items = res.data?.items ?? []
            setTotal(res.data?.total ?? 0)
            if (mode === 'reset') {
              setMembers(items)
              pageRef.current = 1
            } else if (mode === 'append') {
              setMembers((prev) => [...prev, ...items])
              pageRef.current = pageNo
            } else {
              // 轮询：按 userId 就地更新首页项（在线/状态），保留分页与顺序
              setMembers((prev) => {
                const map = new Map(items.map((it) => [it.userId, it]))
                return prev.map((m) => map.get(m.userId) || m)
              })
            }
          })
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          .catch(() => {})
      )
    },
    [dispatch, groupId, date, pageSize]
  )

  /** 重置到首页（挂载 / 切日期） */
  const reset = useCallback(() => {
    if (isMock) {
      setMembers(MOCK_REALTIME_MEMBERS.items)
      setTotal(MOCK_REALTIME_MEMBERS.total)
      pageRef.current = 1
      return
    }
    if (!groupId) return
    setLoading(true)
    fetchPage(1, 'reset').then(() => {
      if (aliveRef.current) setLoading(false)
    })
  }, [isMock, groupId, fetchPage])

  /** 轮询刷新（就地合并首页） */
  const poll = useCallback(() => {
    if (isMock || !groupId) return
    fetchPage(1, 'merge')
  }, [isMock, groupId, fetchPage])

  /** 上拉加载下一页 */
  const loadMore = useCallback(() => {
    if (isMock || loadingMoreRef.current) return
    if (members.length >= total) return
    loadingMoreRef.current = true
    setLoadingMore(true)
    fetchPage(pageRef.current + 1, 'append').then(() => {
      loadingMoreRef.current = false
      if (aliveRef.current) setLoadingMore(false)
    })
  }, [isMock, members.length, total, fetchPage])

  useEffect(() => {
    aliveRef.current = true
    reset()

    // mock 与历史态（非当天）不轮询；仅当天按 60s 轮询刷新成员状态
    if (isMock || !isToday) {
      return () => {
        aliveRef.current = false
      }
    }
    const timer = window.setInterval(poll, pollIntervalMs)
    return () => {
      aliveRef.current = false
      window.clearInterval(timer)
    }
  }, [reset, poll, isMock, isToday, pollIntervalMs])

  return {
    members,
    total,
    loading,
    loadingMore,
    hasMore: !isMock && members.length < total,
    loadMore,
    refresh: reset
  }
}
